/**
 * PreferencesSection
 *
 * Orchestrator component that renders all preference visualizations.
 * Maps each preference type to its appropriate visual treatment.
 */
import { VStack, SimpleGrid, Box } from '@chakra-ui/react'
import { motion } from 'framer-motion'
import type { TasteInsightsData } from '@/types/tasteInsights'
import { PreferenceHeatMeter } from './PreferenceHeatMeter'
import { PreferenceDollarScale } from './PreferenceDollarScale'
import { PreferenceExpandableCard } from './PreferenceExpandableCard'
import { PreferenceBadge } from './PreferenceBadge'
import { PreferenceRestrictionsBadge } from './PreferenceRestrictionsBadge'

const MotionBox = motion.create(Box)

interface PreferencesSectionProps {
  /** The insights data containing all preference values */
  insights: TasteInsightsData
}

export function PreferencesSection({ insights }: PreferencesSectionProps) {
  // Structured object checks - verify required properties exist
  const hasAversions = (insights.aversions?.length ?? 0) > 0
  const hasSpiceTolerance = insights.spiceTolerance && typeof insights.spiceTolerance.level === 'number'
  const hasPriceSensitivity = Boolean(insights.priceSensitivity)
  const hasPortionPreference = Boolean(insights.portionPreference)
  // Expandable prefs are now structured objects with summary/style/frequency + description
  const hasCuisineCuriosity = insights.cuisineCuriosity && Boolean(insights.cuisineCuriosity.summary)
  const hasAtmosphere = insights.atmospherePreference && Boolean(insights.atmospherePreference.style)
  const hasTasteVariance = insights.tasteVariance && Boolean(insights.tasteVariance.frequency)

  // Check if we have any meter/badge preferences (compact row)
  const hasCompactPrefs = hasAversions || hasSpiceTolerance || hasPriceSensitivity || hasPortionPreference
  // Check if we have any expandable preferences
  const hasExpandablePrefs = hasCuisineCuriosity || hasAtmosphere || hasTasteVariance

  if (!hasCompactPrefs && !hasExpandablePrefs) {
    return null
  }

  return (
    <VStack align="stretch" spacing={3}>
      {/* Compact visual preferences - meters and badges in a responsive grid */}
      {hasCompactPrefs && (
        <SimpleGrid columns={2} spacing={3}>
          {/* Restrictions - only rendered when aversions exist */}
          {hasAversions && (
            <MotionBox
              key="restrictions"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15, delay: 0.02 }}
              h="100%"
            >
              <PreferenceRestrictionsBadge aversions={insights.aversions} />
            </MotionBox>
          )}

          {/* Spice Tolerance - Heat Meter */}
          {hasSpiceTolerance && (
            <MotionBox
              key="spice"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15, delay: 0.04 }}
              h="100%"
            >
              <PreferenceHeatMeter value={insights.spiceTolerance} />
            </MotionBox>
          )}

          {/* Price Sensitivity - Dollar Scale */}
          {hasPriceSensitivity && (
            <MotionBox
              key="price"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15, delay: 0.06 }}
              h="100%"
            >
              <PreferenceDollarScale value={insights.priceSensitivity} />
            </MotionBox>
          )}

          {/* Portion Preference - Badge */}
          {hasPortionPreference && (
            <MotionBox
              key="portion"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15, delay: 0.08 }}
              h="100%"
            >
              <PreferenceBadge value={insights.portionPreference} />
            </MotionBox>
          )}
        </SimpleGrid>
      )}

      {/* Expandable preference cards */}
      {hasExpandablePrefs && (
        <VStack align="stretch" spacing={2}>
          {/* Cuisine Curiosity */}
          {hasCuisineCuriosity && (
            <PreferenceExpandableCard
              type="cuisineCuriosity"
              value={insights.cuisineCuriosity}
            />
          )}

          {/* Atmosphere */}
          {hasAtmosphere && (
            <PreferenceExpandableCard
              type="atmosphere"
              value={insights.atmospherePreference}
            />
          )}

          {/* Taste Variance */}
          {hasTasteVariance && (
            <PreferenceExpandableCard
              type="tasteVariance"
              value={insights.tasteVariance}
            />
          )}
        </VStack>
      )}
    </VStack>
  )
}
